import * as React from 'react';

import { SectionBreakCurve } from '@/components';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import { ProfilesResponse } from '@/features/user';
import { useIsSmallScreen } from '@/lib/hooks/use-media-query';

import clockAndBooks from './assets/clock-and-books.webp';
import monitor from './assets/monitor.png';

export interface ConfirmBatchModalProps {
  isOpen?: boolean;
  onClose?: () => void;
  onConfirm?: () => void;
  isLoading?: boolean;
  className?: string;
  children?: React.ReactNode;
  selectedProfile?: ProfilesResponse.Profile;
  doesSelectionIncludesWaitlist: boolean;
  selectedWaitlistReason: string;
}

const ConfirmBatchModal = React.forwardRef<
  HTMLDivElement,
  ConfirmBatchModalProps
>(
  (
    {
      isOpen = false,
      onClose,
      onConfirm,
      isLoading = false,
      children,
      doesSelectionIncludesWaitlist,
      selectedWaitlistReason,
      // selectedProfile,
      ...props
    },
    ref
  ) => {
    // Check if all children have waitlist theme
    const allChildrenAreWaitlist = React.useMemo(() => {
      if (!children) return false;

      // Flatten children to handle fragments properly
      const flattenChildren = (
        children: React.ReactNode
      ): React.ReactElement[] => {
        const result: React.ReactElement[] = [];
        React.Children.forEach(children, child => {
          if (React.isValidElement(child)) {
            if (child.type === React.Fragment) {
              // If it's a fragment, recursively flatten its children
              const fragmentProps = child.props as {
                children?: React.ReactNode;
              };
              result.push(...flattenChildren(fragmentProps.children));
            } else {
              result.push(child);
            }
          }
        });
        return result;
      };

      const validElements = flattenChildren(children);

      if (validElements.length === 0) return false;

      return validElements.every(child => {
        const props = child.props as Record<string, unknown>;
        return props.theme === 'waitlist';
      });
    }, [children]);

    const isSmallScreen = useIsSmallScreen();

    const modalContent = (
      <>
        <div
          className={`${allChildrenAreWaitlist ? 'bg-hot-sun-yellow-light-cardbg' : 'bg-blue-chaos-light-cardbg'} p-1 flex justify-center items-center`}
        >
          <img
            src={allChildrenAreWaitlist ? clockAndBooks : monitor}
            alt={allChildrenAreWaitlist ? 'clock and books' : 'monitor'}
            className="w-35 h-35 object-contain"
          />
        </div>
        <SectionBreakCurve
          topColor={
            allChildrenAreWaitlist
              ? 'var(--color-hot-sun-yellow-light-cardbg)'
              : 'var(--color-blue-chaos-light-cardbg)'
          }
          bottomColor="var(--color-magical-moonlight-white)"
        />
        <div className="p-6 pt-1 flex flex-col gap-1 items-center">
          <h3 className="capitalize font-bold text-magical-moonlight-dark text-xl leading-7 text-center">
            Please double-check the batches you are selecting.
          </h3>
          <p className="font-medium text-muted-foreground text-sm leading-6 text-center">
            You&apos;ll stay in this batch throughout your learning journey.
          </p>
          {/* <div className="flex items-center gap-2 mt-2">
            <img src={noteIcon} alt="" className="w-4 h-4" />
            <span className="font-medium text-magical-moonlight text-sm leading-6">
              1-150 Sessions,
              {selectedProfile?.grade}
            </span>
          </div> */}
          <div className="flex flex-col my-1">{children}</div>
          <div className="flex gap-1 items-center w-full mt-3">
            <div className="flex-1 mx-2 md:mx-6">
              <Button
                variant="outline"
                size={isSmallScreen ? 'sm' : 'lg'}
                fullWidth
                onClick={onClose}
              >
                Cancel
              </Button>
            </div>
            <div className="flex-1 mx-2 md:mx-6">
              <Button
                variant="contained"
                size={isSmallScreen ? 'sm' : 'lg'}
                fullWidth
                onClick={onConfirm}
                disabled={
                  isLoading ||
                  (doesSelectionIncludesWaitlist
                    ? selectedWaitlistReason === ''
                    : false)
                }
              >
                {isLoading ? 'Confirming...' : 'Yes, Confirm'}
              </Button>
            </div>
          </div>
        </div>
      </>
    );

    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size={isSmallScreen ? 'sm' : 'xl'}
        className="bg-magical-moonlight-white rounded-3xl m-auto max-w-[calc(100%-1rem)]"
        ref={ref}
        {...props}
      >
        {modalContent}
      </Modal>
    );
  }
);

ConfirmBatchModal.displayName = 'ConfirmBatchModal';

export { ConfirmBatchModal };
export default ConfirmBatchModal;
